import * as XLSX from 'xlsx';
import { validateValue, splitMulti } from '@vft/core/validation';
import { buildDomainIndex } from '@vft/core/validation/domain.js';

// Separador canònic de multichoice que espera el backend (CommonData.MULTICHOICE_SEPARATOR).
const MULTI_JOIN = '|';

function getFieldId(field) {
  return field.id || field.name || field.label;
}

function normalizeForMap(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim().toLowerCase();
}

// Un camp és multiresposta si el seu màxim de respostes és > 1 (mateix criteri que la
// validació i el valueMap). Aleshores una cel·la pot portar diversos codis units pel
// separador de multiresposta i el remapeig de valors s'ha d'aplicar codi a codi.
function fieldMandatory(field) {
  return field?.validation?.mandatory ?? field?.mandatory ?? null;
}
function isMultiField(field) {
  const m = fieldMandatory(field);
  const max = m?.max ?? field?.max;
  return max != null && max > 1;
}
function fieldSeparators(field) {
  return fieldMandatory(field)?.separators;
}
function getDomain(field) {
  return field?.validation?.domain ?? field?.domain ?? field?.domainValue ?? null;
}

// Normalitza NOMÉS la caixa contra un domini tancat: un codi que ja casa amb el domini
// ignorant majúscules/minúscules (p. ex. "U.1"→"u.1", "No"→"no") es passa a la clau
// canònica, en SILENCI — no és una correcció d'error, no genera proposta. Els codis que
// no casen es deixen igual (els tracta el valueMap / closestDomain). És idempotent.
function canonCode(code, domIndex) {
  const n = normalizeForMap(code);
  if (!n) return code;
  const hit = domIndex.byKey.get(n) || domIndex.byValue.get(n);
  return hit && hit.key != null ? String(hit.key) : code;
}
function canonicalizeDomainCase(value, field, domIndex) {
  if (isMultiField(field)) {
    const pieces = splitMulti(value, fieldSeparators(field));
    if (!pieces.length) return value;
    return pieces.map((p) => canonCode(p, domIndex)).join(MULTI_JOIN);
  }
  return canonCode(String(value), domIndex);
}

export function buildOutputRows({ headers, rows }, fields, mapping, options = {}) {
  const autoValues = options.autoValues || {};
  const columnActions = options.columnActions || {};
  const extraTargets = options.extraTargets || {};
  const valueMap = options.valueMap || {};

  const fieldIds = fields.map(getFieldId);
  const headerIndexByName = new Map(headers.map((h, i) => [h, i]));
  // Índex de domini per camp (una sola vegada) per a la canonicalització de caixa.
  const domainIndexByField = fields.map((f) => {
    const d = getDomain(f);
    return Array.isArray(d) && d.length ? buildDomainIndex(d) : null;
  });

  const outRows = rows.map((row) => {
    const out = new Array(fieldIds.length).fill(null);
    fields.forEach((field, idx) => {
      const fid = fieldIds[idx];
      if (columnActions[fid] === 'ignore') return;
      const colName = mapping[fid];
      if (colName && headerIndexByName.has(colName)) {
        out[idx] = row[headerIndexByName.get(colName)] ?? null;
      } else if (autoValues[fid] != null && autoValues[fid] !== '') {
        out[idx] = autoValues[fid];
      }

      if (extraTargets[fid]) {
        for (const extra of extraTargets[fid]) {
          if (out[idx] == null && headerIndexByName.has(extra)) {
            out[idx] = row[headerIndexByName.get(extra)];
          }
        }
      }

      const fieldMap = valueMap[fid];
      if (fieldMap && out[idx] != null) {
        if (isMultiField(field)) {
          // Camp multiresposta: remapeja CADA codi individualment (p. ex. "U.1|U.99" amb
          // U.99→U.44 queda "U.1|U.44") i torna a unir pel separador canònic. Els codis
          // sense remapeig es conserven tal qual.
          const pieces = splitMulti(out[idx], fieldSeparators(field));
          if (pieces.length) {
            out[idx] = pieces
              .map((p) => {
                const n = normalizeForMap(p);
                return n && Object.prototype.hasOwnProperty.call(fieldMap, n) ? fieldMap[n] : p;
              })
              .join(MULTI_JOIN);
          }
        } else {
          const norm = normalizeForMap(out[idx]);
          if (norm && Object.prototype.hasOwnProperty.call(fieldMap, norm)) {
            out[idx] = fieldMap[norm];
          }
        }
      }

      // Normalització de caixa silenciosa: passa els codis vàlids a la clau canònica del
      // domini (p. ex. "U.1|U.2"→"u.1|u.2"). No és una correcció → no apareix al pas 6.
      const domIndex = domainIndexByField[idx];
      if (domIndex && out[idx] != null && out[idx] !== '') {
        out[idx] = canonicalizeDomainCase(out[idx], field, domIndex);
      }
    });
    return out;
  });

  return { headers: fieldIds, rows: outRows };
}

export function validateOutputRows(outputRows, fields) {
  const fieldIds = fields.map(getFieldId);
  const errorsByRow = [];
  let totalErrors = 0;

  outputRows.rows.forEach((row, rIdx) => {
    const rowErrors = [];
    fields.forEach((field, idx) => {
      const result = validateValue(row[idx], field);
      if (!result.valid) {
        rowErrors.push(...result.errors.map((e) => ({ rowIndex: rIdx, fieldId: fieldIds[idx], ...e })));
      }
    });
    if (rowErrors.length) {
      totalErrors += rowErrors.length;
      errorsByRow.push({ rowIndex: rIdx, errors: rowErrors });
    }
  });

  return {
    totalRows: outputRows.rows.length,
    rowsWithErrors: errorsByRow.length,
    totalErrors,
    errors: errorsByRow,
  };
}

export function buildExportWorkbook(outputRows, errorReport, sourceName) {
  const wb = XLSX.utils.book_new();

  const respMatrix = [outputRows.headers, ...outputRows.rows];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(respMatrix), 'Respuestas');

  const errMatrix = [['Fila', 'Camp', 'Codi', 'Valor', 'Esperat']];
  for (const r of errorReport.errors) {
    for (const e of r.errors) {
      errMatrix.push([e.rowIndex + 1, e.fieldId, e.code, e.value ?? '', e.expected ?? '']);
    }
  }
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(errMatrix), 'Errors');

  const meta = [
    ['Fitxer origen', sourceName || ''],
    ['Files exportades', errorReport.totalRows],
    ['Files amb errors', errorReport.rowsWithErrors],
    ['Total errors', errorReport.totalErrors],
    ['Generat', new Date().toISOString()],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(meta), 'Resum');

  return wb;
}

// Munta un Excel amb els errors retornats per la validació de Sherpa (serverErrors):
// una fila per error amb Fila/Camp/Missatge i el valor actual d'aquell camp a la fila.
export function buildServerErrorsWorkbook(serverErrors, outputRows, sourceName) {
  const wb = XLSX.utils.book_new();
  const errors = serverErrors || [];
  const headers = outputRows?.headers || [];
  const rows = outputRows?.rows || [];
  const matrix = [['Fila', 'Camp', 'Missatge', 'Valor']];
  for (const e of errors) {
    const colIdx = headers.indexOf(e.alias);
    const value = colIdx >= 0 ? (rows[e.rowIndex]?.[colIdx] ?? '') : '';
    matrix.push([e.rowIndex + 1, e.alias ?? '', e.message ?? '', value]);
  }
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(matrix), 'Errors Sherpa');

  const meta = [
    ['Fitxer origen', sourceName || ''],
    ['Total errors', errors.length],
    ['Generat', new Date().toISOString()],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(meta), 'Resum');
  return wb;
}

export function downloadExportWorkbook(wb, sourceName) {
  const filename = `${(sourceName || 'transformat').replace(/\.[^.]+$/, '')}_transformat.xlsx`;
  XLSX.writeFile(wb, filename);
}

// Descàrrega amb nom de fitxer explícit (p. ex. l'Excel del que s'ha carregat).
export function downloadWorkbookAs(wb, filename) {
  XLSX.writeFile(wb, filename);
}

export function workbookToBlob(wb) {
  const arr = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  return new Blob([arr], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}
